import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE, whatsappLink } from "@/lib/site";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Method", href: "#method" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Location", href: "#location" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 flex items-center justify-between transition-all duration-500 ease-out ${
        scrolled ? "h-16 sm:h-[72px]" : "h-20 sm:h-24 lg:h-28"
      }`}>
        <a href="#top" className="flex items-center" data-testid="navbar-logo-link" aria-label="Corrective Motion">
          <img
            src={SITE.iconUrl}
            alt="Corrective Motion"
            className={`w-auto object-contain transition-all duration-500 ease-out ${
              scrolled
                ? "h-10 sm:h-12 drop-shadow-none"
                : "h-16 sm:h-20 lg:h-24 drop-shadow-[0_0_30px_rgba(0,229,255,0.35)]"
            }`}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide hover:shadow-[0_0_30px_-5px_rgba(0,229,255,0.6)] transition-shadow"
          >
            Book Consultation
          </a>
          <button
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-zinc-300 hover:text-white py-2 border-b border-white/5"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center items-center gap-2 bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black px-5 py-3 rounded-full text-sm font-semibold"
                data-testid="navbar-mobile-cta-btn"
              >
                Book Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
